import { FC, useEffect, useState, useContext } from "react";

import { PickerValue } from "../../types";
import api from "../../backendAPI";

import FilterCategoryTitle from "../../components/filterCategoryTitle";
import StyledAsyncCombobox from "../../components/styledAsyncCombobox";

import useSearch, { LocationType } from "../../hooks/useSearch";
import { TranslationContext } from "../../context/TranslationContext";

import { LocationHelperPlace } from "../../backendAPI/General";

type Props = {
  location: string;
};

const LocationPickerHelper: FC<Props> = ({ location }) => {
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const { setLocation, setSelectedPlaceName } = useSearch();

  const { i } = useContext(TranslationContext);

  const [selectedLocation, setSelectedLocation] =
    useState<(PickerValue & LocationHelperPlace) | null>(null);

  useEffect(() => {
    if (!selectedLocation || selectedLocation.id === "0") return;

    setLocation(selectedLocation);
    setIsPickingLocation(false);

    if (selectedLocation.name) {
      setSelectedPlaceName(selectedLocation.name);
    }
  }, [selectedLocation]);

  const handleOpen = () => {
    window.countryId = undefined;

    setIsPickingLocation(true);
  };

  useEffect(
    function toggleListenerOnWindow() {
      if (isPickingLocation) {
        setTimeout(() => {
          window.addEventListener("click", handleClose);
        }, 250);
      } else {
        setTimeout(() => {
          window.removeEventListener("click", handleClose);
        }, 250);
      }

      return () => {
        window.removeEventListener("click", handleClose);
      };
    },

    [isPickingLocation]
  );

  const handleClose = (event: Event) => {
    event.preventDefault();

    const target = event.target as HTMLElement;

    if (target.closest(".DistanceSelectSlider")) return;

    setIsPickingLocation(false);

    window.removeEventListener("click", handleClose);
  };

  async function fetchLocationsFromDB(input: string) {
    const response = await api.general.getPlacesSuggestion(input);

    const values: PickerValue[] = response.data.list.map((i, index) => {
      return Object.assign({}, { id: index, ...i });
    });

    return values;
  }

  return (
    <div className="LocationPickerHelper">
      <div onClick={handleOpen} className="cursor-pointer">
        {!isPickingLocation && (
          <div className="flex">
            <FilterCategoryTitle title={location} />
            <button className="ml-2 mt-0 relative">
              <div className="blob-orange absolute"></div>
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <path
                    d="M17.104 0.852404L5.92796 12.0284C5.5011 12.453 5.1627 12.958 4.93235 13.5142C4.70199 14.0704 4.58426 14.6668 4.58596 15.2688V16.4999C4.58596 16.743 4.68253 16.9762 4.85444 17.1481C5.02635 17.32 5.25951 17.4166 5.50262 17.4166H6.73371C7.33574 17.4183 7.93213 17.3005 8.48835 17.0702C9.04457 16.8398 9.54957 16.5014 9.97412 16.0746L21.1501 4.89857C21.6858 4.36156 21.9867 3.63401 21.9867 2.87549C21.9867 2.11697 21.6858 1.38941 21.1501 0.852404C20.6053 0.331639 19.8807 0.0410156 19.127 0.0410156C18.3734 0.0410156 17.6488 0.331639 17.104 0.852404ZM19.854 3.6024L8.67796 14.7784C8.16107 15.2921 7.46246 15.5813 6.73371 15.5832H6.41929V15.2688C6.4212 14.5401 6.71039 13.8415 7.22412 13.3246L18.4001 2.14857C18.5959 1.96153 18.8563 1.85716 19.127 1.85716C19.3978 1.85716 19.6582 1.96153 19.854 2.14857C20.0464 2.34154 20.1545 2.60295 20.1545 2.87549C20.1545 3.14802 20.0464 3.40943 19.854 3.6024Z"
                    fill="rgb(236,98,59)"
                  />
                  <path
                    d="M21.0833 8.23075C20.8402 8.23075 20.6071 8.32733 20.4352 8.49924C20.2632 8.67114 20.1667 8.9043 20.1667 9.14742V13.75H16.5C15.7707 13.75 15.0712 14.0397 14.5555 14.5555C14.0397 15.0712 13.75 15.7707 13.75 16.5V20.1667H4.58333C3.85399 20.1667 3.15452 19.8769 2.63879 19.3612C2.12306 18.8455 1.83333 18.146 1.83333 17.4167V4.58333C1.83333 3.85399 2.12306 3.15452 2.63879 2.63879C3.15452 2.12306 3.85399 1.83333 4.58333 1.83333H12.8718C13.1149 1.83333 13.3481 1.73676 13.52 1.56485C13.6919 1.39294 13.7885 1.15978 13.7885 0.916667C13.7885 0.673552 13.6919 0.440394 13.52 0.268485C13.3481 0.0965771 13.1149 0 12.8718 0L4.58333 0C3.3682 0.00145554 2.20326 0.484809 1.34404 1.34404C0.484809 2.20326 0.00145554 3.3682 0 4.58333L0 17.4167C0.00145554 18.6318 0.484809 19.7967 1.34404 20.656C2.20326 21.5152 3.3682 21.9985 4.58333 22H14.9811C15.5833 22.0017 16.1798 21.884 16.7362 21.6536C17.2925 21.4233 17.7977 21.0849 18.2224 20.658L20.6571 18.2215C21.084 17.797 21.4225 17.292 21.653 16.7358C21.8836 16.1796 22.0015 15.5832 22 14.9811V9.14742C22 8.9043 21.9034 8.67114 21.7315 8.49924C21.5596 8.32733 21.3265 8.23075 21.0833 8.23075ZM16.9263 19.3618C16.5578 19.7294 16.0918 19.9839 15.5833 20.0952V16.5C15.5833 16.2569 15.6799 16.0237 15.8518 15.8518C16.0237 15.6799 16.2569 15.5833 16.5 15.5833H20.0979C19.9845 16.0907 19.7304 16.5559 19.3646 16.9253L16.9263 19.3618Z"
                    fill="rgb(236,98,59)"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1167_16830">
                    <rect width="22" height="22" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
        )}
      </div>

      {isPickingLocation && (
        <StyledAsyncCombobox
          request={fetchLocationsFromDB}
          // selected={}
          placeholder={i("Find a place...")}
          setSelected={setSelectedLocation}
          alwaysShowAll={true}
        />
      )}
    </div>
  );
};

export default LocationPickerHelper;
